/**
 * Support Chat API Service
 * Handles communication with the support backend
 */

import { api } from './api';

export interface SupportMessage {
  id: string;
  sender_type: 'client' | 'support';
  sender_name: string;
  content: string;
  attachment_url: string | null;
  attachment_type: 'image' | 'document' | null;
  created_at: string;
}

export interface CreateTicketRequest {
  clientName: string;
  clientEmail?: string;
  message: string;
  attachmentUrl?: string;
  attachmentType?: 'image' | 'document';
}

export interface CreateTicketResponse {
  ticketId: string;
  ticketNumber: string;
}

export interface SendMessageRequest {
  message: string;
  attachmentUrl?: string;
  attachmentType?: 'image' | 'document';
}

export interface FetchMessagesResponse {
  messages: SupportMessage[];
  status: 'open' | 'closed';
}

export interface SupportStats {
  totalTickets: number;
  totalReplies: number;
  companies: string[];
  organizationList: Array<{ id: string; name: string }>;
  byClient: Record<string, { tickets: number; replies: number; organizationId?: string }>;
  byMonth: Record<string, { tickets: number; replies: number }>;
  byCompanyMonth: Record<string, Record<string, { tickets: number; replies: number }>>;
}

/**
 * Upload an attachment for support chat. Returns URL and type for use in createTicket/sendMessage.
 */
export const uploadAttachment = async (file: File): Promise<{ url: string; type: 'image' | 'document' }> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post<{ success: boolean; data: { url: string; type: 'image' | 'document' } }>(
    '/support/upload',
    formData
  );
  if (!response.data.success || !response.data.data) {
    throw new Error('Upload failed');
  }
  return response.data.data;
};

/**
 * Create a new support ticket
 */
export const createTicket = async (data: CreateTicketRequest): Promise<CreateTicketResponse> => {
  const response = await api.post('/support/tickets', data);
  return response.data.data;
};

/**
 * Send a message to an existing ticket
 */
export const sendMessage = async (ticketId: string, data: SendMessageRequest): Promise<void> => {
  await api.post(`/support/tickets/${ticketId}/messages`, data);
};

/**
 * Fetch messages for a ticket
 */
export const fetchMessages = async (ticketId: string): Promise<FetchMessagesResponse> => {
  const response = await api.get(`/support/tickets/${ticketId}/messages`);
  return response.data.data;
};

/**
 * Close a support ticket
 */
export const closeTicket = async (ticketId: string, closedBy: string): Promise<void> => {
  await api.put(`/support/tickets/${ticketId}/close`, { closedBy });
};

/**
 * Get support statistics (for admin dashboard)
 */
export const getSupportStats = async (): Promise<SupportStats> => {
  const response = await api.get('/support/stats');
  return response.data.data;
};

export const supportAPI = {
  uploadAttachment,
  createTicket,
  sendMessage,
  fetchMessages,
  closeTicket,
  getSupportStats,
};

export default supportAPI;
